/**
 * Página de detalle de producto - Estilo Awwwards
 * Experiencia inmersiva con animaciones GSAP, parallax y microinteracciones
 */

import React, { useState, useEffect, useRef, useLayoutEffect } from 'react';
import { useParams, Link, useNavigate, useLocation } from 'react-router-dom';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import ImmersiveLayout from '../components/layout/ImmersiveLayout';
import Container from '../components/common/Container';
import { getProductById, getAllRetailProducts } from '../data/retailProductsData';
import { getRecipesByProductType, getProductTypeFromId } from '../data/recipesData';

gsap.registerPlugin(ScrollTrigger);

/**
 * Página de detalle de producto con experiencia inmersiva estilo Awwwards
 * @returns {JSX.Element} Página de detalle inmersiva
 */
const ProductDetailPage = () => {
  const { productId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const product = getProductById(productId);
  const [openSections, setOpenSections] = useState({ descripcion: true, ingredientes: false });
  const [imageLoaded, setImageLoaded] = useState(false);
  const [selectedFormat, setSelectedFormat] = useState(product?.formats?.[0] || null);
  const ingredientesRef = useRef(null);
  const ingredientesContentRef = useRef(null);

  // Función para toggle ingredientes - mantiene la posición visual
  const handleIngredientesToggle = () => {
    const isOpening = !openSections.ingredientes;
    
    // Guardar la posición actual del botón antes de abrir
    const buttonRect = ingredientesRef.current?.getBoundingClientRect();
    const buttonTopBefore = buttonRect?.top || 0;
    
    setOpenSections(prev => ({ ...prev, ingredientes: !prev.ingredientes }));
    
    // Si se está abriendo, compensar el scroll después de que se expanda
    if (isOpening) {
      requestAnimationFrame(() => {
        setTimeout(() => {
          const buttonRectAfter = ingredientesRef.current?.getBoundingClientRect();
          const buttonTopAfter = buttonRectAfter?.top || 0;
          const diff = buttonTopAfter - buttonTopBefore;
          
          // Si el botón se movió, ajustar el scroll para compensar
          if (Math.abs(diff) > 5) {
            window.scrollBy({ top: diff, behavior: 'instant' });
          }
        }, 50);
      });
    }
  };

  // Scroll al inicio cuando cambia el producto - useLayoutEffect para ejecutar antes del pintado
  useLayoutEffect(() => {
    // Scroll inmediato antes de que se pinte la página
    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
    
    // Scroll con Lenis si está disponible
    if (window.__lenis) {
      window.__lenis.scrollTo(0, { immediate: true });
    }
    
    // Reforzar scroll después de que Lenis se inicialice completamente
    const timer = setTimeout(() => {
      window.scrollTo(0, 0);
      if (window.__lenis) {
        window.__lenis.scrollTo(0, { immediate: true });
      }
    }, 100);
    
    return () => clearTimeout(timer);
  }, [productId, location.key]);

  // Sincronizar el formato seleccionado cuando cambia el producto
  useEffect(() => {
    if (product?.formats?.[0]) {
      setSelectedFormat(product.formats[0]);
      setImageLoaded(false);
    }
  }, [productId, product]);

  // Obtener la imagen según el formato seleccionado
  const getCurrentImage = () => {
    if (product?.formatImages && selectedFormat && product.formatImages[selectedFormat]) {
      return product.formatImages[selectedFormat];
    }
    return product?.image;
  };
  
  // Refs para animaciones
  const containerRef = useRef(null);
  const heroRef = useRef(null);
  const imageContainerRef = useRef(null);
  const titleRef = useRef(null);
  const contentRef = useRef(null);
  const recipesRef = useRef(null);
  const ctaRef = useRef(null);

  // Animaciones principales con GSAP
  useLayoutEffect(() => {
    if (!product) return;

    const ctx = gsap.context(() => {
      // Timeline principal
      const tl = gsap.timeline({ defaults: { ease: 'power4.out' } });

      // Animación del título letra por letra
      if (titleRef.current) {
        const chars = titleRef.current.querySelectorAll('.char');
        tl.fromTo(chars, 
          { y: 100, opacity: 0, rotateX: -90 },
          { y: 0, opacity: 1, rotateX: 0, duration: 1, stagger: 0.03 },
          0.2
        );
      }

      // Animación de la imagen con efecto reveal
      if (imageContainerRef.current) {
        tl.fromTo(imageContainerRef.current,
          { clipPath: 'inset(100% 0 0 0)', scale: 1.2 },
          { clipPath: 'inset(0% 0 0 0)', scale: 1, duration: 1.5, ease: 'power4.inOut' },
          0.3
        );
      }

      // Animación del contenido con stagger
      if (contentRef.current) {
        const contentItems = contentRef.current.querySelectorAll('.content-animate');
        tl.fromTo(contentItems,
          { y: 60, opacity: 0 },
          { y: 0, opacity: 1, duration: 0.8, stagger: 0.1 },
          0.8
        );
      }

      // Parallax en la imagen al hacer scroll
      if (imageContainerRef.current) {
        gsap.to(imageContainerRef.current.querySelector('img, .placeholder-image'), {
          yPercent: 20,
          ease: 'none',
          scrollTrigger: {
            trigger: heroRef.current,
            start: 'top top',
            end: 'bottom top',
            scrub: true
          }
        });
      }

      // Animación de entrada para sección de recetas
      if (recipesRef.current) {
        const recipeCards = recipesRef.current.querySelectorAll('.recipe-card');
        gsap.fromTo(recipeCards,
          { y: 100, opacity: 0, scale: 0.9 },
          {
            y: 0,
            opacity: 1,
            scale: 1,
            duration: 0.8,
            stagger: 0.15,
            ease: 'power3.out',
            scrollTrigger: {
              trigger: recipesRef.current,
              start: 'top 80%',
              toggleActions: 'play none none reverse'
            }
          }
        );
      }

      // Animación CTA
      if (ctaRef.current) {
        gsap.fromTo(ctaRef.current.querySelectorAll('.cta-animate'),
          { y: 50, opacity: 0 },
          {
            y: 0,
            opacity: 1,
            duration: 0.8,
            stagger: 0.1,
            scrollTrigger: {
              trigger: ctaRef.current,
              start: 'top 85%',
              toggleActions: 'play none none reverse'
            }
          }
        );
      }

    }, containerRef);

    return () => ctx.revert();
  }, [product]);

  // Obtener recetas relacionadas basadas en el tipo de producto
  const productType = getProductTypeFromId(productId);
  const relatedRecipes = getRecipesByProductType(productType).slice(0, 3);

  // Función para dividir texto en palabras (evita romper palabras a nivel de letras)
  const splitTextToChars = (text) => {
    return text.split(' ').map((word, index, array) => (
      <span 
        key={index} 
        className="char inline-block whitespace-nowrap"
      >
        {word}{index < array.length - 1 ? '\u00A0' : ''}
      </span>
    ));
  };

  // Si no se encuentra el producto
  if (!product) {
    return (
      <ImmersiveLayout>
        <div className="min-h-screen bg-intercasa-dark flex items-center justify-center">
          <Container className="text-center">
            <div className="w-32 h-32 bg-intercasa-red rounded-full flex items-center justify-center mx-auto mb-8 animate-pulse">
              <svg className="w-16 h-16 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
            <h1 className="font-baskerville text-5xl md:text-7xl font-bold text-white mb-6">404</h1>
            <p className="text-xl text-gray-400 mb-12">Producto no encontrado</p>
            <Link
              to="/productos/retail"
              className="group relative inline-flex items-center bg-intercasa-red text-white font-bold px-10 py-5 rounded-full overflow-hidden"
            >
              <span className="relative z-10 group-hover:text-intercasa-red transition-colors duration-300">Volver a productos</span>
              <div className="absolute inset-0 bg-white transform scale-x-0 group-hover:scale-x-100 transition-transform origin-left duration-500"></div>
            </Link>
          </Container>
        </div>
      </ImmersiveLayout>
    );
  }

  return (
    <ImmersiveLayout>
      <div ref={containerRef} className="overflow-hidden">
        {/* Banner - Solo para productos de Ketchup en Cristal */}
        {product.categorySlug === 'ketchup-cristal' && (
          <div className="w-full relative min-h-[25vh] md:min-h-0">
            {/* Banner imagen de fondo */}
            <img 
              src="/images/productos/BANNERS IDEAL PARA/ketchup-normal-zero.webp" 
              alt="Ideal para acompañar tus platos favoritos"
              className="w-full h-full md:h-auto object-cover absolute md:relative inset-0"
            />
            
            {/* Overlay oscuro para mejorar legibilidad */}
            <div className="absolute inset-0 bg-black/40"></div>
            
            {/* Texto superpuesto sobre el banner - Posicionado más abajo */}
            <div className="absolute inset-0 flex flex-col items-center justify-end pb-6 md:pb-4 lg:pb-5 px-4">
              <h3 className="text-intercasa-yellow font-baskerville text-2xl md:text-3xl lg:text-4xl xl:text-5xl font-bold mb-2 md:mb-3 drop-shadow-lg">
                Perfecto para...
              </h3>
              <p className="text-white font-lato text-base md:text-lg lg:text-xl xl:text-2xl text-center max-w-3xl drop-shadow-md">
                Bocadillos, papas fritas, carnes, pastas y cualquier receta rápida en casa
              </p>
            </div>
          </div>
        )}

        {/* Banner - Solo para productos de Ketchup PE */}
        {product.id === 'ketchup-pe' && (
          <div className="w-full relative min-h-[25vh] md:min-h-0">
            {/* Banner imagen de fondo */}
            <img 
              src="/images/productos/BANNERS IDEAL PARA/ketchup-picante-pe.webp" 
              alt="Ideal para acompañar tus platos favoritos"
              className="w-full h-full md:h-auto object-cover absolute md:relative inset-0"
            />
            
            {/* Overlay oscuro para mejorar legibilidad */}
            <div className="absolute inset-0 bg-black/40"></div>
            
            {/* Texto superpuesto sobre el banner - Posicionado más abajo */}
            <div className="absolute inset-0 flex flex-col items-center justify-end pb-6 md:pb-4 lg:pb-5 px-4">
              <h3 className="text-intercasa-yellow font-baskerville text-2xl md:text-3xl lg:text-4xl xl:text-5xl font-bold mb-2 md:mb-3 drop-shadow-lg">
                Perfecto para...
              </h3>
              <p className="text-white font-lato text-base md:text-lg lg:text-xl xl:text-2xl text-center max-w-3xl drop-shadow-md">
                Bocadillos, papas, carnes a la plancha y comidas rápidas en familia
              </p>
            </div>
          </div>
        )}

        {/* Banner - Solo para productos de Mostaza Dulce */}
        {(product.id === 'mostaza-dulce-original-pe' || product.id === 'mostaza-dulce-pe') && (
          <div className="w-full relative min-h-[25vh] md:min-h-0">
            {/* Banner imagen de fondo */}
            <img 
              src="/images/productos/BANNERS IDEAL PARA/mostaza-dulce.webp" 
              alt="Ideal para acompañar tus platos favoritos"
              className="w-full h-full md:h-auto object-cover absolute md:relative inset-0"
            />
            
            {/* Overlay oscuro para mejorar legibilidad */}
            <div className="absolute inset-0 bg-black/40"></div>
            
            {/* Texto superpuesto sobre el banner - Posicionado más abajo */}
            <div className="absolute inset-0 flex flex-col items-center justify-end pb-6 md:pb-4 lg:pb-5 px-4">
              <h3 className="text-intercasa-yellow font-baskerville text-2xl md:text-3xl lg:text-4xl xl:text-5xl font-bold mb-2 md:mb-3 drop-shadow-lg">
                Perfecto para...
              </h3>
              <p className="text-white font-lato text-base md:text-lg lg:text-xl xl:text-2xl text-center max-w-3xl drop-shadow-md">
                Perritos calientes, bocadillos, carnes y recetas que necesitan un toque dulce
              </p>
            </div>
          </div>
        )}

        {/* Banner - Solo para productos de Salsa Barbacoa */}
        {product.id === 'salsa-barbacoa-pe' && (
          <div className="w-full relative min-h-[25vh] md:min-h-0">
            {/* Banner imagen de fondo */}
            <img 
              src="/images/productos/BANNERS IDEAL PARA/Barbacoa.webp" 
              alt="Ideal para acompañar tus platos favoritos"
              className="w-full h-full md:h-auto object-cover absolute md:relative inset-0"
            />
            
            {/* Overlay oscuro para mejorar legibilidad */}
            <div className="absolute inset-0 bg-black/40"></div>
            
            {/* Texto superpuesto sobre el banner - Posicionado más abajo */}
            <div className="absolute inset-0 flex flex-col items-center justify-end pb-6 md:pb-4 lg:pb-5 px-4">
              <h3 className="text-intercasa-yellow font-baskerville text-2xl md:text-3xl lg:text-4xl xl:text-5xl font-bold mb-2 md:mb-3 drop-shadow-lg">
                Perfecto para...
              </h3>
              <p className="text-white font-lato text-base md:text-lg lg:text-xl xl:text-2xl text-center max-w-3xl drop-shadow-md">
                Carnes a la parrilla, hamburguesas, papas y marinados
              </p>
            </div>
          </div>
        )}

        {/* Banner - Solo para productos de Mayonesa */}
        {product.id === 'mayonesa-pe' && (
          <div className="w-full relative min-h-[25vh] md:min-h-0">
            {/* Banner imagen de fondo */}
            <img 
              src="/images/productos/BANNERS IDEAL PARA/Mayonesa.webp" 
              alt="Ideal para acompañar tus platos favoritos"
              className="w-full h-full md:h-auto object-cover absolute md:relative inset-0"
            />
            
            {/* Overlay oscuro para mejorar legibilidad */}
            <div className="absolute inset-0 bg-black/40"></div>
            
            {/* Texto superpuesto sobre el banner - Posicionado más abajo */}
            <div className="absolute inset-0 flex flex-col items-center justify-end pb-6 md:pb-4 lg:pb-5 px-4">
              <h3 className="text-intercasa-yellow font-baskerville text-2xl md:text-3xl lg:text-4xl xl:text-5xl font-bold mb-2 md:mb-3 drop-shadow-lg">
                Perfecto para...
              </h3>
              <p className="text-white font-lato text-base md:text-lg lg:text-xl xl:text-2xl text-center max-w-3xl drop-shadow-md">
                Papas, ensaladas, bocadillos, carnes frías y recetas rápidas para compartir
              </p>
            </div>
          </div>
        )}

        {/* Banner - Solo para productos de Alioli */}
        {product.id === 'alioli-receta-canaria-pe' && (
          <div className="w-full relative min-h-[25vh] md:min-h-0">
            {/* Banner imagen de fondo */}
            <img 
              src="/images/productos/BANNERS IDEAL PARA/Alioli.webp" 
              alt="Ideal para acompañar tus platos favoritos"
              className="w-full h-full md:h-auto object-cover absolute md:relative inset-0"
            />
            
            {/* Overlay oscuro para mejorar legibilidad */}
            <div className="absolute inset-0 bg-black/40"></div>
            
            {/* Texto superpuesto sobre el banner - Posicionado más abajo */}
            <div className="absolute inset-0 flex flex-col items-center justify-end pb-6 md:pb-4 lg:pb-5 px-4">
              <h3 className="text-intercasa-yellow font-baskerville text-2xl md:text-3xl lg:text-4xl xl:text-5xl font-bold mb-2 md:mb-3 drop-shadow-lg">
                Perfecto para...
              </h3>
              <p className="text-white font-lato text-base md:text-lg lg:text-xl xl:text-2xl text-center max-w-3xl drop-shadow-md">
                Papas arrugadas, carnes, pescados, bocadillos y snacks
              </p>
            </div>
          </div>
        )}

        {/* Banner - Solo para productos de Tomates en Conserva */}
        {product.categorySlug === 'tomates-conserva' && (
          <div className="w-full relative min-h-[25vh] md:min-h-0">
            {/* Banner imagen de fondo */}
            <img 
              src="/images/productos/BANNERS IDEAL PARA/ketchup-picante-pe.webp" 
              alt="Ideal para acompañar tus platos favoritos"
              className="w-full h-full md:h-auto object-cover absolute md:relative inset-0"
            />
            
            {/* Overlay oscuro para mejorar legibilidad */}
            <div className="absolute inset-0 bg-black/40"></div>
            
            {/* Texto superpuesto sobre el banner - Posicionado más abajo */}
            <div className="absolute inset-0 flex flex-col items-center justify-end pb-6 md:pb-4 lg:pb-5 px-4">
              <h3 className="text-intercasa-yellow font-baskerville text-2xl md:text-3xl lg:text-4xl xl:text-5xl font-bold mb-2 md:mb-3 drop-shadow-lg">
                Perfecto para...
              </h3>
              <p className="text-white font-lato text-base md:text-lg lg:text-xl xl:text-2xl text-center max-w-3xl drop-shadow-md">
                Salsas, guisos, pastas, arroces y recetas tradicionales
              </p>
            </div>
          </div>
        )}

        {/* Hero Section - Inmersivo Awwwards */}
        <section ref={heroRef} className={`relative bg-intercasa-bone overflow-hidden ${(product.categorySlug === 'ketchup-cristal' || product.id === 'ketchup-pe' || product.id === 'mostaza-dulce-original-pe' || product.id === 'mostaza-dulce-pe' || product.id === 'salsa-barbacoa-pe' || product.id === 'mayonesa-pe' || product.id === 'alioli-receta-canaria-pe' || product.categorySlug === 'tomates-conserva') ? '' : 'min-h-[70vh]'}`}>
          {/* Elementos decorativos animados */}
          <div className="absolute inset-0 pointer-events-none overflow-hidden">
            <div className="absolute -top-20 -right-20 w-96 h-96 bg-intercasa-red/5 rounded-full animate-pulse"></div>
            <div className="absolute -bottom-40 -left-40 w-[500px] h-[500px] bg-yellow-400/5 rounded-full"></div>
            <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] border border-intercasa-red/5 rounded-full"></div>
          </div>

          <Container className={`relative z-10 ${(product.categorySlug === 'ketchup-cristal' || product.id === 'ketchup-pe' || product.id === 'mostaza-dulce-original-pe' || product.id === 'mostaza-dulce-pe' || product.id === 'salsa-barbacoa-pe' || product.id === 'mayonesa-pe' || product.id === 'alioli-receta-canaria-pe' || product.categorySlug === 'tomates-conserva') ? 'py-6 md:py-10 lg:py-12' : 'py-8 md:py-16 lg:py-24'}`}>
            {/* Breadcrumb con animación */}
            <nav className="flex items-center text-xs md:text-sm text-intercasa-gray mb-4 md:mb-6 flex-wrap opacity-0 animate-fadeIn" style={{ animationDelay: '0.5s', animationFillMode: 'forwards' }}>
              <Link to="/productos" className="hover:text-intercasa-red transition-colors duration-300 relative group">
                Productos
                <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-intercasa-red group-hover:w-full transition-all duration-300"></span>
              </Link>
              <span className="mx-2 md:mx-3">/</span>
              <Link to="/productos/retail" className="hover:text-intercasa-red transition-colors duration-300 relative group">
                Retail
                <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-intercasa-red group-hover:w-full transition-all duration-300"></span>
              </Link>
              <span className="mx-2 md:mx-3">/</span>
              <span className="text-intercasa-red font-semibold break-words">{product.name}</span>
            </nav>

            {/* Botón Volver con efecto circular */}
            <Link 
              to="/productos/retail" 
              className="inline-flex items-center text-intercasa-gray hover:text-intercasa-red transition-all duration-300 mb-8 md:mb-16 group"
            >
              <span className="w-12 h-12 rounded-full border-2 border-current flex items-center justify-center mr-4 group-hover:bg-intercasa-red group-hover:border-intercasa-red group-hover:text-white transition-all duration-300">
                <svg className="w-5 h-5 transform group-hover:-translate-x-1 transition-transform duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </span>
              <span className="font-medium text-lg">Volver</span>
            </Link>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-16 lg:gap-24 items-center">
              {/* Imagen del producto */}
              <div ref={imageContainerRef} className="relative order-2 lg:order-1">
                <div className="relative bg-white rounded-2xl md:rounded-[2rem] overflow-hidden">
                  <div className="aspect-[3/4] md:aspect-square bg-white p-4 md:p-6">
                    {getCurrentImage() ? (
                      <img 
                        src={getCurrentImage()} 
                        alt={`${product.name} ${selectedFormat}`} 
                        className="w-full h-full object-contain transition-opacity duration-300"
                        key={selectedFormat}
                        onLoad={() => setImageLoaded(true)}
                      />
                    ) : (
                      <div className="placeholder-image w-full h-full flex items-center justify-center">
                        <div className="text-center">
                          <div className="w-32 h-32 mx-auto mb-6 bg-intercasa-red/10 rounded-full flex items-center justify-center">
                            <svg className="w-16 h-16 text-intercasa-red opacity-50" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z" clipRule="evenodd" />
                            </svg>
                          </div>
                          <p className="text-intercasa-gray font-medium">Imagen próximamente</p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Información del producto */}
              <div ref={contentRef} className="order-1 lg:order-2 px-2 md:px-0">
                {/* Título con animación de caracteres */}
                <h1 
                  ref={titleRef}
                  className="font-baskerville text-5xl md:text-6xl lg:text-7xl xl:text-8xl font-bold text-intercasa-dark mb-6 md:mb-10 leading-tight tracking-tight"
                  style={{ perspective: '1000px' }}
                >
                  {splitTextToChars(product.name)}
                </h1>

                {/* Tamaños disponibles */}
                <div className="content-animate mb-8 md:mb-10">
                  <p className="text-intercasa-gray font-medium mb-4 text-sm uppercase tracking-widest">Formatos disponibles</p>
                  <div className="flex flex-wrap gap-3">
                    {product.formats.map((format, index) => (
                      <button 
                        key={index}
                        onClick={() => setSelectedFormat(format)}
                        className={`group relative px-6 py-3 font-bold rounded-full overflow-hidden transition-all duration-300 ${
                          selectedFormat === format
                            ? 'bg-intercasa-red border-2 border-intercasa-red text-white'
                            : 'bg-transparent border-2 border-intercasa-dark text-intercasa-dark hover:border-intercasa-red'
                        }`}
                      >
                        <span className={`relative z-10 transition-colors duration-300 ${
                          selectedFormat === format ? 'text-white' : 'group-hover:text-white'
                        }`}>{format}</span>
                        {selectedFormat !== format && (
                          <div className="absolute inset-0 bg-intercasa-red transform scale-x-0 group-hover:scale-x-100 transition-transform origin-left duration-300"></div>
                        )}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Categoría */}
                <div className="content-animate flex items-center mb-8 md:mb-10">
                  <span className="text-intercasa-gray font-medium mr-3 text-sm uppercase tracking-widest">Categoría</span>
                  <span className="px-5 py-2 bg-intercasa-red text-white rounded-full font-bold text-sm">
                    {product.category}
                  </span>
                </div>

                {/* Acordeones con animación suave */}
                <div className="content-animate space-y-4">
                  {/* Ingredientes */}
                  <div 
                    ref={ingredientesRef}
                    className="border-2 border-gray-200 rounded-2xl overflow-hidden hover:border-intercasa-red/30 transition-colors duration-300 scroll-mt-24"
                  >
                    <button
                      onClick={handleIngredientesToggle}
                      className="w-full flex items-center justify-between px-6 py-5 bg-white hover:bg-gray-50 transition-colors duration-300"
                    >
                      <span className="font-bold text-intercasa-dark flex items-center text-lg">
                        <span className="w-10 h-10 rounded-full bg-intercasa-red/10 flex items-center justify-center mr-4">
                          <svg className="w-5 h-5 text-intercasa-red" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
                          </svg>
                        </span>
                        Ingredientes
                      </span>
                      <span className={`w-10 h-10 rounded-full bg-intercasa-dark flex items-center justify-center transition-transform duration-500 ${openSections.ingredientes ? 'rotate-180' : ''}`}>
                        <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </span>
                    </button>
                    <div 
                      className="grid transition-all duration-500 ease-out"
                      style={{ 
                        gridTemplateRows: openSections.ingredientes ? '1fr' : '0fr',
                      }}
                    >
                      <div className="overflow-hidden">
                        <div className="px-6 py-6 bg-intercasa-bone/50 border-t border-gray-100">
                          <p className="text-intercasa-gray leading-relaxed text-lg">
                            {product.ingredients}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </Container>
        </section>

        {/* Sección de Recetas - Estilo Awwwards */}
        <section ref={recipesRef} className="relative bg-intercasa-red py-20 md:py-32 overflow-hidden">
          {/* Elementos decorativos */}
          <div className="absolute inset-0 pointer-events-none">
            <div className="absolute -top-20 right-0 w-96 h-96 bg-white/5 rounded-full"></div>
            <div className="absolute -bottom-20 left-0 w-96 h-96 bg-black/10 rounded-full"></div>
          </div>

          <Container className="relative z-10">
            <div className="text-center mb-16 md:mb-20">
              <span className="inline-block px-6 py-2 bg-white/20 rounded-full text-white/90 text-sm font-medium mb-6">
                Inspírate
              </span>
              <h2 className="font-baskerville text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6 leading-tight">
                Cocina con <span className="text-yellow-300 break-words">{product.name}</span>
              </h2>
              <p className="text-center text-lg md:text-xl text-white/70 max-w-2xl mx-auto">
                Descubre recetas deliciosas para preparar con este producto
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
              {relatedRecipes.map((recipe, index) => (
                <Link 
                  key={recipe.id} 
                  to={`/recetas/${recipe.slug}`}
                  className="recipe-card group relative bg-white rounded-2xl md:rounded-3xl overflow-hidden shadow-2xl transform hover:-translate-y-4 transition-all duration-500"
                  style={{ transitionDelay: `${index * 100}ms` }}
                >
                  {/* Overlay de hover */}
                  <div className="absolute inset-0 bg-intercasa-dark/80 opacity-0 group-hover:opacity-100 transition-opacity duration-500 z-10 flex items-center justify-center">
                    <span className="text-white font-bold text-lg transform translate-y-4 group-hover:translate-y-0 transition-transform duration-500">
                      Ver receta →
                    </span>
                  </div>

                  <div className="aspect-[4/3] bg-intercasa-light overflow-hidden">
                    {recipe.imageUrl ? (
                      <img 
                        src={recipe.imageUrl} 
                        alt={recipe.title}
                        className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-700"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-intercasa-light to-intercasa-bone">
                        <svg className="w-20 h-20 text-intercasa-red opacity-20" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z" clipRule="evenodd" />
                        </svg>
                      </div>
                    )}
                  </div>
                  <div className="p-6 md:p-8">
                    <h3 className="font-bold text-intercasa-dark text-xl mb-3 group-hover:text-intercasa-red transition-colors duration-300">
                      {recipe.title}
                    </h3>
                    <p className="text-sm text-intercasa-gray line-clamp-2">
                      {recipe.description}
                    </p>
                  </div>
                </Link>
              ))}
            </div>

            <div className="text-center mt-16">
              <Link
                to="/recetas"
                className="group relative inline-flex items-center bg-white text-intercasa-red font-bold px-10 py-5 rounded-full overflow-hidden shadow-xl hover:shadow-2xl transition-shadow duration-300"
              >
                <span className="relative z-10 group-hover:text-white transition-colors duration-300">Ver Todas Las Recetas</span>
                <svg className="w-5 h-5 ml-3 relative z-10 transform group-hover:translate-x-2 group-hover:text-white transition-all duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                </svg>
                <div className="absolute inset-0 bg-intercasa-dark transform scale-x-0 group-hover:scale-x-100 transition-transform origin-left duration-500"></div>
              </Link>
            </div>
          </Container>
        </section>

        {/* CTA Final - Estilo Awwwards */}
        <section ref={ctaRef} className="relative bg-intercasa-bone py-20 md:py-32 overflow-hidden">
          {/* Patrón de fondo */}
          <div className="absolute inset-0 pointer-events-none">
            <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] border border-intercasa-dark/5 rounded-full"></div>
            <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[400px] h-[400px] border border-intercasa-dark/10 rounded-full"></div>
          </div>

          <Container className="relative z-10">
            <div className="max-w-4xl mx-auto text-center">
              <h3 className="cta-animate font-baskerville text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-intercasa-dark mb-8 leading-tight">
                ¿Te interesa<br />
                <span className="text-intercasa-red">este producto?</span>
              </h3>
              <p className="cta-animate text-lg md:text-xl text-intercasa-gray mb-12 max-w-2xl mx-auto">
                Descubre toda nuestra gama de productos o contáctanos para más información
              </p>
              <div className="cta-animate flex flex-col sm:flex-row gap-4 md:gap-6 justify-center">
                <Link
                  to="/productos/retail"
                  className="group relative inline-flex items-center justify-center bg-intercasa-red text-white font-bold px-8 md:px-10 py-4 md:py-5 rounded-full overflow-hidden shadow-lg hover:shadow-xl transition-shadow duration-300"
                >
                  <span className="relative z-10">Ver todos los productos</span>
                  <div className="absolute inset-0 bg-intercasa-dark transform scale-x-0 group-hover:scale-x-100 transition-transform origin-left duration-500"></div>
                </Link>
                <Link
                  to="/contacto"
                  className="group relative inline-flex items-center justify-center bg-transparent border-2 border-intercasa-dark text-intercasa-dark font-bold px-8 md:px-10 py-4 md:py-5 rounded-full overflow-hidden transition-all duration-300 hover:border-intercasa-red"
                >
                  <span className="relative z-10 group-hover:text-white transition-colors duration-300">Contactar</span>
                  <div className="absolute inset-0 bg-intercasa-red transform scale-x-0 group-hover:scale-x-100 transition-transform origin-left duration-500"></div>
                </Link>
              </div>
            </div>
          </Container>
        </section>
      </div>
    </ImmersiveLayout>
  );
};

export default ProductDetailPage;
